import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, FormMessage, Input, Tooltip } from '~/components';
import { useApi } from '~/contexts';
import { colors } from '~/styles';
import { useAdminSession } from '../contexts';
import ChangeStatus from './components/ChangeStatus';
import CreateInvoiceSubscription from './components/CreateInvoiceSubscription';
import EditNotes from './components/EditNotes';
import SetTrial from './components/SetTrial';

const ActionList = styled.div`
  display: flex;
  padding: 1rem 1.25rem;
  border-top: solid 1px ${colors.grey10};

  > :not(:first-child) {
    margin-left: 1rem;
  }
`;

const CloneForm = styled.form`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
`;

export default function Actions({ workspace, onChange }) {
  const api = useApi();
  const { can } = useAdminSession();
  const [changeStatus, setChangeStatus] = useState();
  const [editNotes, setEditNotes] = useState();
  const [setTrial, setSetTrial] = useState();
  const [createSubscription, setCreateSubscription] = useState();
  const [isLoadingDemoData, setIsLoadingDemoData] = useState(false);
  const [isCloneFormVisible, setIsCloneFormVisible] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [cloneError, setCloneError] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceKey, setNewWorkspaceKey] = useState('');

  const hasStripeSubscription = !!workspace.subscriptionStatus;
  const billingNotRequired = workspace.billing?.isRequired === false;
  const trialDisabled = hasStripeSubscription || billingNotRequired;
  const trialDisabledReason = hasStripeSubscription
    ? 'Trial is managed by Stripe'
    : billingNotRequired
      ? 'Billing is not required for this workspace'
      : null;

  const canSubscribe = !!workspace.billing?.stripeCustomerId && !workspace.subscriptionStatus;

  const handleChange = () => {
    if (onChange) {
      onChange(workspace);
    }
  };

  const handleDemoDataRequest = async () => {
    setIsLoadingDemoData(true);
    try {
      await api.admin.workspaces.createDemoData(workspace.id);
      handleChange();
    } finally {
      setIsLoadingDemoData(false);
    }
  };

  const handleCloneWorkspace = async (event) => {
    event.preventDefault();
    setCloneError(null);
    setIsCloning(true);
    try {
      await api.admin.workspaces.clone({
        workspaceId: workspace.id,
        workspaceName: newWorkspaceName,
        workspaceKey: newWorkspaceKey,
      });

      // Reset form after successful clone
      setNewWorkspaceName('');
      setNewWorkspaceKey('');
      setIsCloneFormVisible(false);
      handleChange();
    } catch (error) {
      setCloneError(error?.message || 'Unable to clone workspace.');
    } finally {
      setIsCloning(false);
    }
  };

  if (!can('workspaces')) return null;

  return (
    <>
      <ActionList>
        <Button
          isOutline={true}
          onClick={(event) => {
            event.stopPropagation();
            setChangeStatus(workspace);
          }}>
          Change Status
        </Button>
        <Tooltip message={trialDisabledReason}>
          <Button
            isOutline={true}
            disabled={trialDisabled}
            onClick={(event) => {
              event.stopPropagation();
              setSetTrial(workspace);
            }}>
            Set Trial
          </Button>
        </Tooltip>
        {canSubscribe && (
          <Button
            isOutline={true}
            onClick={(event) => {
              event.stopPropagation();
              setCreateSubscription(workspace);
            }}>
            Create Invoice Subscription
          </Button>
        )}
        <Button
          isOutline={true}
          onClick={(event) => {
            event.stopPropagation();
            setEditNotes(workspace);
          }}>
          Edit Notes
        </Button>
        <Tooltip message="This feature is currently disabled.">
          <Button isOutline={true} isLoading={isLoadingDemoData} onClick={handleDemoDataRequest} disabled>
            Load Sample Data
          </Button>
        </Tooltip>
        <Button isOutline={true} onClick={() => setIsCloneFormVisible(!isCloneFormVisible)}>
          Clone Workspace
        </Button>
      </ActionList>
      {isCloneFormVisible && (
        <CloneForm onSubmit={handleCloneWorkspace}>
          {cloneError && <FormMessage.Error>{cloneError}</FormMessage.Error>}
          Clone this workspace by providing the name and key. The new workspace will copy all custom data and members.
          <Input
            type="text"
            placeholder="New Workspace Name"
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            required
          />
          <Input
            type="text"
            placeholder="New Workspace Key"
            value={newWorkspaceKey}
            onChange={(e) => setNewWorkspaceKey(e.target.value)}
            required
          />
          <FormActions>
            <Button type="submit" isLoading={isCloning}>
              Clone
            </Button>
            <Button type="button" onClick={() => setIsCloneFormVisible(false)}>
              Cancel
            </Button>
          </FormActions>
        </CloneForm>
      )}
      {changeStatus && (
        <ChangeStatus workspace={changeStatus} onChange={handleChange} onClose={() => setChangeStatus()} />
      )}
      {editNotes && <EditNotes workspace={editNotes} onChange={handleChange} onClose={() => setEditNotes()} />}
      {setTrial && <SetTrial workspace={setTrial} onChange={handleChange} onClose={() => setSetTrial()} />}
      {createSubscription && (
        <CreateInvoiceSubscription
          workspace={createSubscription}
          onChange={handleChange}
          onClose={() => setCreateSubscription()}
        />
      )}
    </>
  );
}
